import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth, API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  TrendingDown, TrendingUp, Timer, AlertTriangle, MessageSquare,
  ClipboardCheck, Image as ImageIcon, Users, Award, ArrowRight,
  FileWarning, Gauge, BellRing, AlertCircle, Loader2,
} from 'lucide-react';

/**
 * Test-insights surface for the Apps Dashboard.
 *
 * Everything here is deterministic aggregation over `test_approvals`
 * (no LLM). When the LLM triage layer is added later we'll render the
 * category breakdowns inside the same widgets without a restructure.
 */

const COLOR = {
  cyan: '#00D4E4', lime: '#BBFF71', coral: '#FC5442',
  amber: '#F59E0B', emerald: '#10B981', rose: '#F43F5E',
  slate: '#94A3B8',
};

const fmtHours = (h) => {
  if (h === null || h === undefined) return '—';
  if (h < 24) return `${Math.round(h)} h`;
  return `${Math.round((h / 24) * 10) / 10} d`;
};

const Delta = ({ value, suffix = 'pp', invertColor = false }) => {
  if (value === null || value === undefined || value === 0) {
    return <span className="text-white/60 text-sm">no change</span>;
  }
  const isUp = value > 0;
  // For rejection rate, up = bad. invertColor flips the semantic.
  const isGood = invertColor ? !isUp : isUp;
  const color = isGood ? 'text-emerald-400' : 'text-rose-400';
  const Icon = isUp ? TrendingUp : TrendingDown;
  return (
    <span className={`flex items-center gap-1 text-sm ${color}`}>
      <Icon className="w-3.5 h-3.5" />
      {isUp ? '+' : ''}{value}{suffix}
    </span>
  );
};

const MiniKPI = ({ label, value, delta, deltaSuffix, deltaInvert, icon: Icon, accent, sub, testid, onClick }) => (
  <Card
    className={`bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] transition-colors ${onClick ? 'cursor-pointer hover:bg-[rgba(229,239,250,0.3)]' : ''}`}
    onClick={onClick}
    data-testid={testid}
  >
    <CardContent className="p-5">
      <div className="flex items-start justify-between mb-3">
        <span className="text-xs font-medium uppercase tracking-wider text-white/80">{label}</span>
        {Icon && (
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${accent}20` }}
          >
            <Icon className="w-4.5 h-4.5" style={{ color: accent }} />
          </div>
        )}
      </div>
      <div className="flex items-end gap-2">
        <div className="text-3xl font-bold text-white leading-none">{value}</div>
        {delta !== undefined && <Delta value={delta} suffix={deltaSuffix} invertColor={deltaInvert} />}
      </div>
      {sub && <div className="text-xs text-white/60 mt-2">{sub}</div>}
    </CardContent>
  </Card>
);

const TrendBars = ({ trend, onWeekClick }) => {
  // Square-root scaling so a week with 1 test is still visible next to a
  // week with 25. Linear scaling squashes everything to invisibility as
  // soon as one week spikes.
  const rawMax = Math.max(1, ...trend.map((w) => w.rejected + w.approved));
  const scale = (v) => (v > 0 ? Math.sqrt(v / rawMax) : 0);
  const MIN_PX = 4;            // any non-zero bar at least this tall
  const CHART_HEIGHT_PX = 180;
  const USABLE_PX = CHART_HEIGHT_PX - 20; // leaves room for the count label

  return (
    <div data-testid="test-insights-trend">
      <div className="flex items-end gap-1" style={{ height: CHART_HEIGHT_PX }}>
        {trend.map((w, i) => {
          const total = w.approved + w.rejected;
          const totalFrac = scale(total);
          const aFrac = total > 0 ? w.approved / total : 0;
          const rFrac = total > 0 ? w.rejected / total : 0;
          const barPx = total > 0 ? Math.max(MIN_PX, totalFrac * USABLE_PX) : 0;
          const approvedPx = total > 0 ? Math.max(w.approved > 0 ? 2 : 0, aFrac * barPx) : 0;
          const rejectedPx = total > 0 ? Math.max(w.rejected > 0 ? 2 : 0, rFrac * barPx) : 0;
          const clickable = Boolean(onWeekClick && w.rejected > 0);

          return (
            <div
              key={i}
              role={clickable ? 'button' : undefined}
              tabIndex={clickable ? 0 : undefined}
              onClick={clickable ? () => onWeekClick(w.week_start) : undefined}
              onKeyDown={
                clickable
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onWeekClick(w.week_start);
                      }
                    }
                  : undefined
              }
              className={`flex-1 relative h-full flex flex-col justify-end items-center group rounded-sm transition-colors ${
                clickable ? 'cursor-pointer hover:bg-[rgba(229,239,250,0.08)]' : ''
              }`}
              title={`Week of ${w.week_start}\n${w.approved} approved, ${w.rejected} rejected${clickable ? '\nClick to drill through' : ''}`}
              data-testid={`trend-bar-${i}`}
            >
              {/* Count label above the bar -- only when there's data */}
              {total > 0 && (
                <div
                  className="absolute left-0 right-0 text-center text-[11px] font-medium text-white/90 select-none pointer-events-none transition-opacity opacity-80 group-hover:opacity-100"
                  style={{ bottom: barPx + 4 }}
                >
                  {total}
                </div>
              )}
              {/* Stacked bar -- rejected on top, approved on bottom */}
              <div className="w-full max-w-[44px] mx-auto flex flex-col-reverse" style={{ height: barPx }}>
                <div style={{ height: approvedPx, backgroundColor: COLOR.emerald }} className="rounded-b-sm" />
                <div
                  style={{ height: rejectedPx, backgroundColor: COLOR.rose }}
                  className={rejectedPx > 0 ? 'rounded-t-sm' : ''}
                />
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-1 mt-1">
        {trend.map((w, i) => (
          <div key={i} className="flex-1 text-center text-[10px] text-white/50 select-none">
            {w.week_start.slice(5)}
          </div>
        ))}
      </div>
    </div>
  );
};

const TestInsights = () => {
  const navigate = useNavigate();
  const { getAccessToken, demoMode } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [remindingToken, setRemindingToken] = useState(null);

  const goFailures = (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    navigate(qs ? `/failures?${qs}` : '/failures');
  };

  const sendRemindersForGroup = async (tokens) => {
    const list = (tokens || []).filter(Boolean);
    if (list.length === 0) return;
    // Use the first token as the "busy" key so the UI disables the button
    // while all reminds for that group are in flight.
    setRemindingToken(list[0]);
    try {
      const headers = demoMode ? {} : (await getAccessToken()
        .then((t) => (t ? { Authorization: `Bearer ${t}` } : {}))
        .catch(() => ({})));
      const results = await Promise.allSettled(
        list.map((t) => axios.post(`${API}/test-approval/${t}/remind`, {}, { headers }))
      );
      const sent = results.filter((r) => r.status === "fulfilled").length;
      const failed = results.length - sent;
      if (sent && !failed) {
        toast.success(`Reminded ${sent} tester${sent === 1 ? "" : "s"}`);
      } else if (sent && failed) {
        toast.warning(`Reminded ${sent}, ${failed} failed`);
      } else {
        toast.error("No reminders could be sent — check email config");
      }
    } finally {
      setRemindingToken(null);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const headers = demoMode ? {} : (await getAccessToken()
          .then((t) => (t ? { Authorization: `Bearer ${t}` } : {}))
          .catch(() => ({})));
        const res = await axios.get(`${API}/analytics/test-insights`, { headers });
        setData(res.data);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Failed to load test insights');
      } finally {
        setLoading(false);
      }
    })();
  }, [demoMode, getAccessToken]);

  if (loading) {
    return (
      <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] mt-6">
        <CardContent className="p-6 text-center text-white/70">Loading test insights…</CardContent>
      </Card>
    );
  }
  if (error || !data) {
    return (
      <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] mt-6">
        <CardContent className="p-6 text-center text-rose-400 text-sm">
          {error || 'No test insights available'}
        </CardContent>
      </Card>
    );
  }

  const { kpis, trend, publisher_leaderboard, recurring_offenders, tester_activity, evidence_completeness, outstanding_tests } = data;

  // If there's no test activity at all, show a friendly empty state.
  const hasActivity =
    kpis.responded_in_window > 0 ||
    kpis.rejections_this_month > 0 ||
    kpis.rejections_last_month > 0 ||
    kpis.pending_responses > 0;

  if (!hasActivity) {
    return (
      <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)] mt-6" data-testid="test-insights-empty">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            <Gauge className="w-5 h-5 text-cyan-400" />
            Test Insights
          </CardTitle>
        </CardHeader>
        <CardContent className="text-center py-10 text-white/70">
          <ClipboardCheck className="w-10 h-10 text-white/30 mx-auto mb-3" />
          <p>No tester responses recorded yet.</p>
          <p className="text-xs text-white/50 mt-1">Insights appear here once testers start approving or rejecting packages.</p>
        </CardContent>
      </Card>
    );
  }

  const monthDelta = kpis.rejections_this_month - kpis.rejections_last_month;

  return (
    <div className="mt-8 space-y-6" data-testid="test-insights-section">
      {/* Section header */}
      <div className="flex items-center gap-3">
        <Gauge className="w-5 h-5 text-cyan-400" />
        <h2 className="text-xl font-semibold text-white">Test Insights</h2>
        <span className="text-xs text-white/50">last {kpis.rejection_rate_window_days} days</span>
      </div>

      {/* KPI strip */}
      <div className="alice-kpi-row grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MiniKPI
          label="Rejection rate"
          value={`${kpis.rejection_rate_pct}%`}
          delta={kpis.rejection_rate_delta_pp}
          deltaSuffix="pp"
          deltaInvert
          icon={FileWarning}
          accent={COLOR.rose}
          sub={`${kpis.responded_in_window} responses in window`}
          testid="kpi-rejection-rate"
          onClick={() => goFailures({ filter: 'window-30d' })}
        />
        <MiniKPI
          label="Avg test time"
          value={fmtHours(kpis.avg_test_time_hours)}
          icon={Timer}
          accent={COLOR.amber}
          sub={kpis.completed_test_cycles
            ? `avg across ${kpis.completed_test_cycles} completed cycle${kpis.completed_test_cycles === 1 ? '' : 's'}`
            : 'no completed cycles yet'}
          testid="kpi-avg-test-time"
          onClick={() => goFailures({ include_resolved: 'true', window_days: '365' })}
        />
        <MiniKPI
          label="Rejections this month"
          value={kpis.rejections_this_month}
          delta={monthDelta}
          deltaSuffix=""
          deltaInvert
          icon={AlertTriangle}
          accent={COLOR.coral}
          sub={`vs ${kpis.rejections_last_month} last month`}
          testid="kpi-rejections-month"
          onClick={() => goFailures({ filter: 'this-month' })}
        />
        <MiniKPI
          label="Open rejections"
          value={kpis.open_rejections}
          icon={ArrowRight}
          accent={COLOR.amber}
          sub={kpis.pending_responses ? `${kpis.pending_responses} awaiting response` : 'no pending responses'}
          testid="kpi-open-rejections"
          onClick={() => goFailures({ filter: 'open' })}
        />
      </div>

      {/* Trend + evidence completeness */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <TrendingUp className="w-5 h-5 text-rose-400" />
              Approvals vs rejections &mdash; last 12 weeks
            </CardTitle>
          </CardHeader>
          <CardContent>
            <TrendBars trend={trend} onWeekClick={(ws) => goFailures({ week: ws, include_resolved: 'true', window_days: '365' })} />
            <div className="flex items-center gap-4 mt-4 text-xs text-white/80">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLOR.emerald }} /> Approved
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: COLOR.rose }} /> Rejected
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)]" data-testid="evidence-completeness-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <ClipboardCheck className="w-5 h-5 text-cyan-400" />
              Evidence completeness
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {[
              { label: 'Comments', pct: evidence_completeness.with_comments_pct, icon: MessageSquare, color: COLOR.cyan },
              { label: 'Test steps', pct: evidence_completeness.with_steps_pct, icon: ClipboardCheck, color: COLOR.lime },
              { label: 'Screenshots', pct: evidence_completeness.with_images_pct, icon: ImageIcon, color: COLOR.amber },
            ].map((r) => (
              <div key={r.label}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="flex items-center gap-2 text-white/90">
                    <r.icon className="w-4 h-4" style={{ color: r.color }} />
                    {r.label}
                  </span>
                  <span className="text-white font-medium">{r.pct}%</span>
                </div>
                <div className="h-1.5 bg-[rgba(229,239,250,0.1)] rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${r.pct}%`, backgroundColor: r.color }}
                  />
                </div>
              </div>
            ))}
            <p className="text-xs text-white/50 pt-1">
              Share of {evidence_completeness.responded_total} responses that included each artefact.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Leaderboards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)]" data-testid="publisher-leaderboard-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <Award className="w-5 h-5 text-amber-400" />
              Publisher leaderboard &mdash; rejection volume
            </CardTitle>
          </CardHeader>
          <CardContent>
            {publisher_leaderboard.length === 0 ? (
              <p className="text-sm text-white/60 py-6 text-center">No rejections recorded.</p>
            ) : (
              <div className="space-y-2">
                {publisher_leaderboard.map((row, i) => (
                  <div
                    key={row.publisher}
                    className="flex items-center gap-3 py-2 border-b border-[rgba(229,239,250,0.08)] last:border-0 cursor-pointer hover:bg-[rgba(229,239,250,0.06)] rounded px-1 -mx-1"
                    onClick={() => goFailures({ publisher: row.publisher })}
                    data-testid={`publisher-row-${i}`}
                  >
                    <div className="w-6 text-center text-white/60 text-sm">{i + 1}</div>
                    <div className="flex-1">
                      <div className="text-white text-sm font-medium">{row.publisher}</div>
                      <div className="h-1.5 bg-[rgba(229,239,250,0.1)] rounded-full overflow-hidden mt-1">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${row.percent}%`, backgroundColor: COLOR.rose }}
                        />
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-white font-semibold text-sm">{row.count}</div>
                      <div className="text-white/50 text-xs">{row.percent}%</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)]" data-testid="recurring-offenders-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-white flex items-center gap-2 text-lg">
              <AlertTriangle className="w-5 h-5 text-rose-400" />
              Recurring offenders
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recurring_offenders.length === 0 ? (
              <p className="text-sm text-white/60 py-6 text-center">No app has been rejected more than once. 🎉</p>
            ) : (
              <div className="space-y-2">
                {recurring_offenders.map((row, i) => (
                  <div
                    key={row.app_name + i}
                    className="flex items-center gap-3 py-2 border-b border-[rgba(229,239,250,0.08)] last:border-0 cursor-pointer hover:bg-[rgba(229,239,250,0.06)] rounded px-1 -mx-1"
                    onClick={() => row.package_id ? goFailures({ package_id: row.package_id, include_resolved: 'true', window_days: '365' }) : undefined}
                    data-testid={`offender-row-${i}`}
                  >
                    <div className="flex-1">
                      <div className="text-white text-sm font-medium">{row.app_name}</div>
                      <div className="text-white/60 text-xs">
                        {row.publisher}
                        {row.last_reject && <> · last reject {new Date(row.last_reject).toLocaleDateString()}</>}
                      </div>
                    </div>
                    <div className="px-2.5 py-1 rounded-md text-xs font-semibold" style={{ backgroundColor: `${COLOR.rose}20`, color: COLOR.rose }}>
                      {row.count}× rejected
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tester activity */}
      <Card className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)]" data-testid="tester-activity-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-white flex items-center gap-2 text-lg">
            <Users className="w-5 h-5 text-cyan-400" />
            Tester activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {tester_activity.length === 0 ? (
            <p className="text-sm text-white/60 py-6 text-center">No testers have been asked to review yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white/70 text-xs uppercase tracking-wider">
                    <th className="text-left font-medium pb-2">Tester</th>
                    <th className="text-center font-medium pb-2">Requests</th>
                    <th className="text-center font-medium pb-2">Response rate</th>
                    <th className="text-center font-medium pb-2">Avg respond</th>
                    <th className="text-center font-medium pb-2">Approved</th>
                    <th className="text-center font-medium pb-2">Rejected</th>
                    <th className="text-center font-medium pb-2">Evidence</th>
                  </tr>
                </thead>
                <tbody>
                  {tester_activity.map((t, i) => (
                    <tr
                      key={t.tester_email}
                      className="border-t border-[rgba(229,239,250,0.08)] cursor-pointer hover:bg-[rgba(229,239,250,0.06)]"
                      onClick={() => goFailures({ tester: t.tester_email })}
                      data-testid={`tester-row-${i}`}
                    >
                      <td className="py-2 text-white">{t.tester_email}</td>
                      <td className="py-2 text-center text-white/90">{t.total}</td>
                      <td className="py-2 text-center">
                        <span className={t.response_rate_pct >= 80 ? 'text-emerald-400' : t.response_rate_pct >= 50 ? 'text-amber-400' : 'text-rose-400'}>
                          {t.response_rate_pct}%
                        </span>
                      </td>
                      <td className="py-2 text-center text-white/90">{fmtHours(t.avg_response_hours)}</td>
                      <td className="py-2 text-center text-emerald-400">{t.approved}</td>
                      <td className="py-2 text-center text-rose-400">{t.rejected}</td>
                      <td className="py-2 text-center text-white/90">{t.evidence_completeness_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Outstanding tests — nudge stale/stuck pending approvals */}
      {outstanding_tests && outstanding_tests.length > 0 && (
        <Card
          className="bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px] border-[rgba(229,239,250,0.1)]"
          data-testid="outstanding-tests-card"
        >
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-base">
              <AlertCircle className="w-4 h-4 text-amber-400" />
              Outstanding tests
              <span className="text-xs text-white/60 font-normal ml-2">
                {outstanding_tests.length} app{outstanding_tests.length === 1 ? '' : 's'}
                {kpis.outstanding_stuck > 0 && (
                  <span className="ml-2 text-rose-400 font-semibold">
                    · {kpis.outstanding_stuck} stuck &gt; 7d
                  </span>
                )}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="outstanding-tests-table">
                <thead>
                  <tr className="text-left text-xs uppercase text-white/60 border-b border-[rgba(229,239,250,0.1)]">
                    <th className="px-5 py-3 font-medium">App</th>
                    <th className="px-5 py-3 font-medium">Testers</th>
                    <th className="px-5 py-3 font-medium">Oldest age</th>
                    <th className="px-5 py-3 font-medium">Severity</th>
                    <th className="px-5 py-3 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {outstanding_tests.map((o) => {
                    const sev = o.worst_severity;
                    const sevMeta = {
                      fresh:  { label: 'Fresh',  cls: 'bg-emerald-500/20 text-emerald-300', dot: 'bg-emerald-400' },
                      aging:  { label: 'Aging',  cls: 'bg-amber-500/20 text-amber-300',     dot: 'bg-amber-400' },
                      stale:  { label: 'Stale',  cls: 'bg-orange-500/20 text-orange-300',   dot: 'bg-orange-400' },
                      stuck:  { label: 'Stuck · escalate', cls: 'bg-rose-500/20 text-rose-300', dot: 'bg-rose-400' },
                    }[sev] || { label: sev, cls: 'bg-white/10 text-white/80', dot: 'bg-white/40' };
                    const ageDisplay = o.oldest_age_hours < 24
                      ? `${Math.round(o.oldest_age_hours)} h`
                      : `${Math.round((o.oldest_age_hours / 24) * 10) / 10} d`;
                    const testerTooltip = (o.tester_emails || []).join(", ");
                    const busy = remindingToken && (o.tokens || []).includes(remindingToken);
                    return (
                      <tr
                        key={o.package_id}
                        className="border-b border-[rgba(229,239,250,0.05)] hover:bg-[rgba(229,239,250,0.04)]"
                        data-testid={`outstanding-row-${o.package_id}`}
                      >
                        <td className="px-5 py-3 text-white">
                          <div className="font-medium">{o.app_name}</div>
                          {o.version && <div className="text-xs text-white/60">v{o.version}</div>}
                        </td>
                        <td className="px-5 py-3 text-white/80" title={testerTooltip}>
                          <span className="tabular-nums">
                            {o.outstanding_testers} of {o.total_testers}
                          </span>
                          <div className="text-xs text-white/50 truncate max-w-[240px]">
                            {testerTooltip}
                          </div>
                        </td>
                        <td className="px-5 py-3 text-white/80 tabular-nums">{ageDisplay}</td>
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs ${sevMeta.cls}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${sevMeta.dot}`} />
                            {sevMeta.label}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={busy || !o.tokens || o.tokens.length === 0}
                            onClick={() => sendRemindersForGroup(o.tokens)}
                            className="bg-[rgba(229,239,250,0.1)] text-white border-[rgba(229,239,250,0.2)] hover:bg-[rgba(229,239,250,0.2)]"
                            data-testid={`remind-btn-${o.package_id}`}
                          >
                            {busy ? (
                              <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                            ) : (
                              <BellRing className="w-3.5 h-3.5 mr-1" />
                            )}
                            Remind all
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TestInsights;
